package ui.resource

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.lazy.staggeredgrid.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import api.DefaultWebAppThumbIcon
import api.ResourceOrientation
import api.ResourceType
import common.frontend.api.Io
import common.frontend.api.isSuperAdmin
import compose.assets.Svg
import compose.assets.svg.*
import org.ssoft.common.tools.calculateAspectRatio
import org.ssoft.common.type.AspectRatio
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.findRoot
import tools.rememberString
import tools.roundBytes
import ui.resource.detail.ResourceArgs
import ui.resource.editor.VisualEditorArgs
import ui.widget.*
import kotlin.math.max
import kotlin.math.min
import kotlin.time.Duration.Companion.milliseconds

@Stable
data class ResourceListingArgs(
    var companyId: Long = -1L,
    var pickerMode: Boolean = false,
    var restrictionType: Int = ResourceType.UNDEFINED.mask,
    var restrictionOrientation: Int = ResourceOrientation.UNDEFINED.mask,
    var restrictionAspectRatio: Int = AspectRatio.NONE.mask,
    var restrictionDurationMax: Long = 0L,
    var restrictionDurationMin: Long = 0L,
    var restrictionCanIgnoreAspectRatio: Boolean = false,
    var restrictionCanIgnoreDuration: Boolean = false,
    var excluded: String = ""
)

@ComposableNavNode(ResourceListingArgs::class)
@Composable
fun ResourceListingUI() {
    val args = LocalNavigationArgs.current
    val arguments = args.rememberArgs(ResourceListingArgs::class)
    val nav = LocalNavigation.current

    ResourceListingInlineUI(
        args = arguments,
        multiselect = false,
        excluded = arguments.excluded
            .split(",")
            .mapNotNull { it.trim().toLongOrNull() },
        nav = nav,
        onPick = if (arguments.pickerMode) { _ -> nav.up() } else null
    )
}

@Composable
private fun ResourceListingInlineUI(
    args: ResourceListingArgs,
    multiselect: Boolean,
    excluded: List<Long>,
    nav: Navigation?,

    onPick: ((Set<Io.ListingResource.Item>) -> Unit)?
) {
    with(ResourceListingScope.rememberScope(args, multiselect, excluded, onPick)) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl, nav)
        }
    }
}

@Composable
private fun ResourceListingScope.Content(nnvl: ResourceListingScope.Wrapper, nav: Navigation?) {

    val searchFiled = remember {
        mutableStateOf(TextFieldValue(""))
    }

    val ignoreRatioRestrictions = remember {
        mutableStateOf(args.restrictionCanIgnoreAspectRatio)
    }
    val ignoreDurationRestrictions = remember {
        mutableStateOf(args.restrictionCanIgnoreDuration)
    }

    val gridSpecs =
        if (display.isMobile) StaggeredGridCells.Fixed(2)
        else StaggeredGridCells.FixedSize(256.dp)
    Column(Modifier.fillMaxSize().padding(top = 8.dp).padding(horizontal = 8.dp)) {
        Header(searchFiled, nnvl)
        Space(16.dp)
        Box(Modifier.fillMaxSize()) {
            LazyVerticalStaggeredGrid(
                modifier = Modifier.fillMaxWidth(),
                columns = gridSpecs,
                verticalItemSpacing = 16.dp,
                horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.Start),
            ) {

            if ((args.restrictionCanIgnoreAspectRatio && (AspectRatio.NONE.mask != args.restrictionAspectRatio))
                    || (args.restrictionCanIgnoreDuration && args.restrictionDurationMin != 0L)
                ) {
                    item(span = StaggeredGridItemSpan.FullLine) {
                        if (display.isMobile || display.resolution.width < 600) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                RestrictionSwitchRatio(Modifier.fillMaxWidth(), ignoreRatioRestrictions)
                                RestrictionSwitchDuration(Modifier.fillMaxWidth(), ignoreDurationRestrictions)
                            }
                        } else {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                RestrictionSwitchRatio(Modifier, ignoreRatioRestrictions)
                                RestrictionSwitchDuration(Modifier, ignoreDurationRestrictions)
                            }
                        }
                    }
                }

                itemsIndexed(
                    SearchWrapper.use(
                        fields = arrayOf(Io.ListingResource.Item::name),
                        items = nnvl.state.filter { it.data.id !in excluded },
                        predicate = {
                            nnvl.selectedType.value.match(it) &&
                                    vm.selectedOrientation.value.match(it) &&
                                    args.isAspectRatioValid(it.data, ignoreRatioRestrictions.value) &&
                                    args.isDurationValid(it.data, ignoreDurationRestrictions.value)
                        },
                        query = searchFiled,
                        sort = compareByDescending { it.data.id }
                    )) { _, item ->
                    ResourceItem(nnvl, item) {
                        nav?.findRoot()
                            ?.navTo_ResourceUI(
                                ResourceArgs(
                                    companyId = args.companyId,
                                    resourceId = item.data.id
                                )
                            )
                    }
                }
            }
            ResourceAddButton(nnvl, Modifier.align(Alignment.BottomEnd).padding(24.dp))
            if (onPick == null) {
                UsedSpace(
                    modifier = Modifier.align(Alignment.BottomStart).padding(24.dp),
                    space = nnvl.space
                )
            }
        }
    }
}

@Composable
private fun ResourceListingScope.RestrictionSwitchDuration(
    modifier: Modifier,
    ignoreDurationRestrictions: MutableState<Boolean>
) {
    if (args.restrictionCanIgnoreDuration && args.restrictionDurationMin != 0L) {
        Card(modifier = modifier, onClick = { ignoreDurationRestrictions.value = !ignoreDurationRestrictions.value }) {
            Row(Modifier.padding(horizontal = 12.dp)) {
                IconicSwitch(
                    checked = ignoreDurationRestrictions.value,
                    onCheckedChange = { ignoreDurationRestrictions.value = it })
                Space(8.dp)
                Text(
                    text = Res.string.media_listing__action_ignore_duration.rememberString(),
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    }
}

@Composable
private fun ResourceListingScope.RestrictionSwitchRatio(
    modifier: Modifier,
    ignoreRatioRestrictions: MutableState<Boolean>
) {
    if (args.restrictionCanIgnoreAspectRatio && (AspectRatio.NONE.mask != args.restrictionAspectRatio)) {
        Card(modifier = modifier, onClick = { ignoreRatioRestrictions.value = !ignoreRatioRestrictions.value }) {
            Row(Modifier.padding(horizontal = 12.dp)) {
                IconicSwitch(
                    checked = ignoreRatioRestrictions.value,
                    onCheckedChange = {
                        ignoreRatioRestrictions.value = it
                    })
                Space(8.dp)
                Text(
                    text = Res.string.media_listing__action_ignore_aspect_ratio.rememberString(),
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    }
}

@Composable
private fun UsedSpace(modifier: Modifier, space: Io.ListingResource.Space) {
    var showDetail by remember {
        mutableStateOf(false)
    }
    val color = remember {
        val pct = (1 - (space.total.toDouble() / Io.ListingResource.Space.LIMIT_SPACE.toDouble()).coerceIn(0.0, 1.0))
        when {
            pct < .2 -> Color.Red
            pct < .5 -> Color.Yellow
            else -> Color.White
        }
    }
    Box(modifier) {
        FloatingActionButton(
            onClick = { showDetail = !showDetail },
            contentColor = color,
            containerColor = MaterialTheme.colorScheme.surface
        ) {
            Icon(
                imageVector = Svg.Database,
                contentDescription = null
            )
        }
        DropdownMenu(
            expanded = showDetail,
            onDismissRequest = { showDetail = false }) {
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .clickable { showDetail = false }
                    .width(256.dp)
                    .padding(8.dp)
            ) {
                TwoFieldText(
                    label = Res.string.media_listing__storage_in_use.rememberString(),
                    message = roundBytes(space.active) { "0 MB" })
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_listing__storage_unused.rememberString(),
                    message = roundBytes(space.unused) { "0 MB" })
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_listing__storage_trash.rememberString(),
                    message = roundBytes(space.trash) { "0 MB" })
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_listing__storage_total.rememberString(),
                    message = roundBytes(space.total) { "0 MB" })
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_listing__storage_free.rememberString(),
                    message = roundBytes(Io.ListingResource.Space.LIMIT_SPACE - space.total) { "0 MB" })
            }
        }
    }
}

@Composable
private fun ResourceListingScope.Header(
    searchFiled: MutableState<TextFieldValue>,
    nnvl: ResourceListingScope.Wrapper,

    ) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        onPick?.also {
            IconButton(
                onClick = { it(setOf()) },
                colors = IconButtonDefaults.iconButtonColors(
                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                    contentColor = MaterialTheme.colorScheme.onPrimaryContainer
                ),
                modifier = Modifier.align(Alignment.CenterVertically)
                    .padding(horizontal = 4.dp)
            ) {
                Icon(
                    Svg.Arrow_left,
                    null,
                    modifier = Modifier.padding(4.dp)
                )
            }

            SearchUI(
                filed = searchFiled,
                modifier = if (display.isMobile) Modifier.weight(1F) else Modifier.width(256.dp),
                label = Res.string.media_listing__search
            )
        } ?: run {
            if (display.isMobile) {
                HomeSearchUI(
                    filed = searchFiled,
                    modifier = Modifier.weight(1F),
                    label = Res.string.media_listing__search
                )
            } else {
                SearchUI(
                    filed = searchFiled,
                    modifier = Modifier.width(256.dp),
                    label = Res.string.media_listing__search
                )
            }
        }

        if (resTypes.size > 1) {
            if (display.isMobile || display.resolution.width < 1000) {
                DropDownPickerButton(
                    selected = nnvl.selectedType,
                    values = resTypes,
                    itemWidth = 192.dp,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            } else {
                DropDownPickerCard(
                    selected = nnvl.selectedType,
                    values = resTypes,
                    itemWidth = 192.dp,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
        if (resOrientations.size > 1) {
            if (display.isMobile || display.resolution.width < 1000) {
                DropDownPickerButton(
                    selected = vm.selectedOrientation,
                    values = resOrientations,
                    itemWidth = 192.dp,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            } else {
                DropDownPickerCard(
                    selected = vm.selectedOrientation,
                    values = resOrientations,
                    itemWidth = 192.dp,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
        if (!display.isMobile) {
            Spacer(Modifier.weight(1F))
        }

        onPick.takeIf { multiselect }
            ?.takeIf { vm.selected.isNotEmpty() }
            ?.also {
                IconButton(
                    onClick = { it(vm.selected) },
                    colors = IconButtonDefaults.iconButtonColors(
                        containerColor = MaterialTheme.colorScheme.primaryContainer,
                        contentColor = MaterialTheme.colorScheme.onPrimaryContainer
                    ),
                    modifier = Modifier.align(Alignment.CenterVertically)
                        .padding(horizontal = 4.dp)
                ) {
                    Icon(
                        Svg.Check_all,
                        null,
                        modifier = Modifier.padding(4.dp)
                    )
                }
            }
    }
}

@Composable
private fun ResourceListingScope.ResourceAddButton(nnvl: ResourceListingScope.Wrapper, modifier: Modifier) {
    var isDropDownExpanded by remember { mutableStateOf(false) }

    @Composable
    fun PickerDialog(items: List<ResourceListingScope.PickerType>) {
        Box(modifier) {
            DropdownMenu(
                expanded = isDropDownExpanded,
                onDismissRequest = { isDropDownExpanded = false },
                offset = DpOffset(0.dp, (-16).dp)
            ) {
                items.forEach {
                    if (it.customVideoFormat) HorizontalDivider(Modifier.padding(horizontal = 8.dp))
                    DropdownMenuItem(
                        text = { Text(it.label.rememberString()) },
                        onClick = {
                            action(ResourceListingVM.Action.AddLocalMedia(it))
                            isDropDownExpanded = false
                        },
                        leadingIcon = { Icon(it.image ?: Svg.Alert, null) })
                }

            }
            FloatingActionButton(
                onClick = { isDropDownExpanded = true },
            ) { Icon(Svg.Plus, null) }
        }
    }

    when (val uState = vm.uploadState) {
        is DataState.Idle -> {
            if (nnvl.canAdd) {
                when {
                    nnvl.selectedType.value.type == ResourceType.UNDEFINED -> {
                        PickerDialog(
                            resTypes
                                .filterNot { it.type == ResourceType.UNDEFINED }
                                .let {
                                    if (nnvl.canCustomVideoType) it + ResourceListingScope.PickerType.customVideoType
                                    else it
                                })
                    }

                    nnvl.selectedType.value.type == ResourceType.VIDEO && nnvl.canCustomVideoType -> {
                        PickerDialog(
                            resTypes
                                .filter { it.type == ResourceType.VIDEO }
                                .plus(ResourceListingScope.PickerType.customVideoType))
                    }

                    else -> {
                        FloatingActionButton(
                            onClick = {
                                action(ResourceListingVM.Action.AddLocalMedia(nnvl.selectedType.value))
                            },
                            modifier = modifier
                        ) { Icon(Svg.Plus, null) }
                    }
                }
            }
        }

        is DataState.Loading,
        is DataState.Result -> {
            uState.entry?.also { state ->
                Card(modifier = modifier) {
                    Text(
                        text = Res.string.media_listing__upload_process.rememberString(
                            (state.success + state.failure),
                            state.all
                        ),
                        modifier = Modifier.padding(16.dp)
                    )
                }
            }
        }

        is DataState.Error -> Unit
    }
}

private fun ResourceListingArgs.isAspectRatioValid(
    item: Io.ListingResource.Item,
    ignoreRatioRestrictions: Boolean
): Boolean {
    return (AspectRatio.NONE.mask == restrictionAspectRatio
            || ignoreRatioRestrictions
            || restrictionAspectRatio == item.aspectRatio)
}

private fun ResourceListingArgs.isDurationValid(
    item: Io.ListingResource.Item,
    ignoreDurationRestrictions: Boolean
): Boolean {
    return restrictionDurationMin == 0L
            || ignoreDurationRestrictions
            || ResourceType.IMAGE match item.type
            || (ResourceType.VIDEO match item.type && item.duration in restrictionDurationMin..restrictionDurationMax)
}

@Composable
private fun ResourceListingScope.ResourceItem(
    nnvl: ResourceListingScope.Wrapper,
    item: ResourceListingScope.Wrapper.Item,
    showDetail: () -> Unit
) {

    var ratio by remember(item) {
        mutableStateOf<Pair<Int, Int>?>(null)
    }

    LaunchedEffect(item) {
        if (!(ResourceType.WWW_APP match item.data.type) && !(ResourceType.AUDIO match item.data.type)) {
            ratio = (AspectRatio[item.data.aspectRatio]
                .firstOrNull()
                ?.pair
                ?: calculateAspectRatio(item.data.width, item.data.height))
                .let {
                    if (item.data.width > item.data.height) max(it.first, it.second) to min(it.first, it.second)
                    else min(it.first, it.second) to max(it.first, it.second)
                }
        }
    }

    val image = ResourceType.rememberIcon(item.data.type)

    val layoutRation = remember(item) {
        runCatching {
            item.data.width.toFloat() / item.data.height.toFloat()
        }.getOrNull() ?: 1F
    }

    Box {
        Card(
            Modifier.fillMaxWidth()
                .clip(CardDefaults.shape)
                .clickable {
                    // TODO make remove onPick
                    if (args.pickerMode || onPick != null) {
                        action(ResourceListingVM.Action.OnItemSelected(item))
                    } else {
                        showDetail()
                    }
                }) {
            Box {
                if (ResourceType.WWW_APP match item.data.type) {
                    LazyImage(
                        id = ResourceType.DefaultWebAppThumbIcon,
                        contentScale = ContentScale.FillWidth,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(
                                ratio = layoutRation,
                                matchHeightConstraintsFirst = true
                            )
                    )
                } else if (ResourceType.AUDIO match item.data.type) {
                    Icon(
                        imageVector = ResourceType.AUDIO.imageVector(),
                        contentDescription = null,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(2F)
                    )
                } else {
                    LazyImage(
                        id = item.data.id,
                        contentScale = ContentScale.FillWidth,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(
                                ratio = layoutRation,
                                matchHeightConstraintsFirst = true
                            )
                    )
                }
                if (args.pickerMode && nnvl.canEdit) {
                    val nav = LocalNavigation.current
                    SmallFloatingActionButton(
                        onClick = {
                            nav.navTo_VisualEditorUI(
                                VisualEditorArgs(
                                    companyId = args.companyId,
                                    resourceId = item.data.id,
                                    upStack = 1
                                )
                            )
                        },
                        modifier = Modifier.align(Alignment.TopEnd)
                            .padding(8.dp)
                    ) {
                        Icon(Svg.Edit, null)
                    }
                }

                this@Card.AnimatedVisibility(
                    visible = multiselect && vm.selected.contains(item.data),
                    enter = fadeIn(),
                    exit = fadeOut(),
                    modifier = Modifier.align(Alignment.TopEnd)
                ) {
                    Icon(
                        imageVector = Svg.Check_all,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onTertiaryContainer,
                        modifier = Modifier.padding(4.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.tertiaryContainer)
                            .padding(4.dp)
                    )
                }
            }
            Column(Modifier.background(Color.Black).padding(5.dp).fillMaxWidth()) {
                Row(Modifier.fillMaxWidth()) {
                    Icon(image, item.data.name)
                    Space(4.dp)
                    if (ResourceType.AUDIO match item.data.type) {
                        Text(
                            text = item.data.duration.milliseconds.toString(),
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            textAlign = TextAlign.End,
                            modifier = Modifier.align(Alignment.CenterVertically)
                                .weight(1F)
                        )
                    } else {
                        Text(
                            text = "${item.data.width}x${item.data.height}",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            modifier = Modifier.weight(1F)
                                .align(Alignment.CenterVertically)
                        )
                    }
                    if (ResourceType.VIDEO match item.data.type) {
                        Space(4.dp)
                        Text(
                            text = item.data.duration.milliseconds.toString(),
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                    ratio?.also {
                        Space(4.dp)
                        Text(
                            text = "(${it.first}:${it.second})",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                }
                Text(
                    text = item.name.text.text,
                    maxLines = 2,
                    style = MaterialTheme.typography.labelSmall,
                    overflow = TextOverflow.Ellipsis,
                    color = Color.White,
                    modifier = Modifier.padding(2.dp)
                )
            }
        }
        if (login.isSuperAdmin()) {
            Card(Modifier.padding(4.dp)) {
                SelectionContainer {
                    Text(
                        text = item.data.id.toString(),
                        style = MaterialTheme.typography.labelSmall,
                        modifier = Modifier.padding(4.dp)
                    )
                }
            }
        }
    }
}

@Composable
fun ResourceType.Companion.rememberIcon(type: Int): ImageVector = remember(type) {
    ResourceType[type].imageVector()
}

fun ResourceType.imageVector(): ImageVector {
    return when (this) {
        ResourceType.IMAGE -> Svg.Image
        ResourceType.ICON -> Svg.Image
        ResourceType.DOCUMENT -> Svg.Pdf
        ResourceType.UNDEFINED -> Svg.Multimedia
        ResourceType.VIDEO -> Svg.Video
        ResourceType.VIDEO_ALPHA -> Svg.VideoAlpha
        ResourceType.WWW_APP -> Svg.Application_brackets_outline
        ResourceType.AUDIO -> Svg.Music_note
        ResourceType.RAW,
        ResourceType.TEXT -> Svg.Alert
    }
}
